import $settings from 'src/include/settings.js';
import $log from 'src/include/log.js';
import $cfetch from 'src/include/cfetch.js';
import $UserSession from 'src/include/usersession.js';

/**
 * Class used for hierarchy manipulation.
 * @class
 * 
 * Bear in mind that a hierarchy is an object that stems from the given id goes as follows
 * [
  {
    "id": "1",
    "root_id": null,
    "parent_id": null,
    "title": "Clinics",
    "note": null,
    "curriculumcomponenttype_id": null,
    "absolute_parent_id": null,
    "children": [
      {
        "id": "4",
        "root_id": "1",
        "parent_id": "1",
        "title": "Y1-2",
        "note": null,
        "curriculumcomponenttype_id": null,
        "absolute_parent_id": "1",
        "children": [
        .
        .
        .
            CHILDREN NODES
        .
        .
        .
            
        ],
    "learningoutcomes": {
      "original": [
        "10",
        "238"
      ],
      "linked": []
    }
  }
]
 */

class CurriculumMap {
    constructor(id , learningoutcomes , userSession = $UserSession){
        this.$UserSession = userSession;
        this.$cfetch = $cfetch;
        this.$log = $log;
        this.$settings = $settings;
        this.id = id;
        this.learningoutcomes = learningoutcomes;
    }

    /**
        * The hierarchy emanating from the given root node
        * 
        * @param {boolean} [breadcrumbs_in_string=false] in case we want to add breadcrums to each node
        * @returns {<Promise>Object} once the promise is fulfilled,
        * it will return an object both with the nodes and their structured hierarchical form.
        *
    */

    getHierarchy(breadcrumbs_in_string = false) {
        const data = {
            id: this.id,
            breadcrumbs_in_string : breadcrumbs_in_string
        };

        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");
        
        return this.$cfetch(`${this.$settings.APIBaseURL}/curriculummap/gethierarchy`, {
            method: "post",
            data: data
        }).then((res) => {
            if (res.status === "SUCCESS") {
                let hierarchy = res.result[0];

                const joint_ids = [
                    ...hierarchy.learningoutcomes.original,
                    ...hierarchy.learningoutcomes.linked
                ]

                if(this.learningoutcomes) hierarchy.learningoutcomes.joint = this.learningoutcomes.filter(obj => joint_ids.includes(obj.id))
                
                return hierarchy
            }
        }).catch(error => {
            this.$log.error.call(this, error);
        })
    }

    /**
        * The hierarchy emanating from the given root node
        * 
        * @param {Object} filters - the user can filter by
        * - parent_id[Number]: the id of the parent node.
        * - show_deleted[boolean]: if we want to show deleted nodes.
        * - search[string]: query for node titles
        * - just_roots[boolean]: if we want to show nodes without parent
        * - path_in_string[boolean]: if we want to add paths to the response
        * @returns {<Promise>Object} once the promise is fulfilled,
        * it will return an object both with the nodes and their structured hierarchical form.
        *
    */

    getNodes(filters) {
        const data = filters || {};
        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");
        
        return this.$cfetch(`${this.$settings.APIBaseURL}/curriculummap/list`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== "SUCCESS") throw new Error('Can not fetch all nodes') 
            return res.results
            
        }).catch(error => {
            this.$log.error.call(this, error);
        })
    }

    /**
        * Gets a node from a hierarchy
        * @param {Object} hierarchy - The hierarchy, an object stemming from a given node
        * @param {Number} id - The id of the node we are searching for
        * 
        *
    */
    getNode(hierarchy, id) {
        
        if (hierarchy.id === id) return hierarchy; // Check if the current node's id matches the targetId

        for (let child of hierarchy.children) { // Iterate over the children and search recursively
            const result = this.getNode(child, id);
            if (result) return result; // If found, return the node
        }

        return null; // If not found anywhere, return null
    }

    /*addPathToNodes(array){
        // We take an array and add the paths in allNodes obtained from getNodes

        // First, we create a map of the result array objects by id
        const nodesMap = new Map(
            array.map(obj => [obj.id, obj])
        );

        // Then, we add additional keys and filter out objects not shared by both arrays
        array = this.nodes
            .map(obj => {
                const additionalInfo = nodesMap.get(obj.id);
                return additionalInfo ? { ...obj, ...additionalInfo } : null;
            })
            .filter(obj => obj !== null);

        return array;
    }*/

      /**
        * Saves a node. If there are parent_id and/or root_id, it is saved as a children of the that parent node with that root.
        *
        * @param {Object} node - The node chosen from the hierarchy.
        * @param {string} parent_id - The id of the parent node.
        * @param {string} root_id - The id of the root node.
        * @param {string} action - action for transmutation, it can be either 'copy' or 'move'
     */
    saveNode(node, parent_id = null, root_id = null, action = null) { //Action
        
        //this.saving = true;
        if (!node) {
            throw new Error('Not a valid node');
        }

        if (parent_id) node.parent_id = parent_id
        if (root_id) node.root_id = root_id;
        if (node.learningoutcomes?.original) {
            const original_ids = node.learningoutcomes.original;

            // Remember that joint is an array of objects while original and linked are arrays of ids.
            const lo_tosave = original_ids && original_ids.length > 0
                ? node.learningoutcomes.joint_obj.filter(obj => node.learningoutcomes.original.includes(obj.id))
                : node.learningoutcomes.joint_obj;

            node.learningoutcomes = lo_tosave;
        }
        
        node.action = action ? action : null;

        return this.$cfetch(`${this.$settings.APIBaseURL}/curriculummap/save`, {
            method: "post",
            data: node
        })
        .then((res) => {
            if (res.status !== 'SUCCESS') throw new Error('Could not save node') 
            return {
                node: res.result,
                newChild: !node.absolute_parent_id
            };
        })
        .catch(error => {
            this.$log.error.call(this, error);
        })
    }
    
    /**
     * 
     * @param {Object} data - an object with the following structure :
     * {
     *      id: [Number] - the node's ID,
     *      spare_children: [Boolean] - truthful if we do not want to delete the node's children.
     *      parent_id: [Number] - the is of the node's parent
     * } 
     * @returns {Boolean} - the status of the deletion operation
     */
    deleteNode(node) {
        const data = node || {};
        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");
        
        return this.$cfetch(`${this.$settings.APIBaseURL}/curriculummap/delete`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== 'SUCCESS') throw new Error('Could not delete node')
            return res.status;
                
        }).catch(error => {
            this.$log.error.call(this, error);
        })
    }  
}

export default CurriculumMap;